import { useMemo, useState } from "react";
import {
  CatalogDirectoryCategory,
  CatalogDirectoryProduct,
  CatalogHeroProduct,
} from "../api/fetchProductCatalogDirectory";
import { useCatalogDirectory } from "./useCatalogDirectory";

export interface CatalogSectionData {
  category: CatalogDirectoryCategory;
  products: CatalogDirectoryProduct[];
}

export function useProductCatalog() {
  const { categories, loading } = useCatalogDirectory();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );

  const heroProducts = useMemo<CatalogHeroProduct[]>(() => {
    return categories
      .flatMap((category) =>
        category.products
          .filter((product) => product.featured)
          .map((product) => ({ ...product, categoryName: category.name })),
      )
      .sort(
        (a, b) =>
          (a.featuredOrder ?? Number.MAX_SAFE_INTEGER) -
          (b.featuredOrder ?? Number.MAX_SAFE_INTEGER),
      );
  }, [categories]);

  const countsByCategoryId = useMemo(() => {
    const counts: Record<string, number> = {};
    categories.forEach((category) => {
      counts[category.id] = category.products.length;
    });
    return counts;
  }, [categories]);

  const totalCount = useMemo(
    () =>
      categories.reduce((sum, category) => sum + category.products.length, 0),
    [categories],
  );

  const sections = useMemo<CatalogSectionData[]>(() => {
    return categories
      .filter(
        (category) =>
          selectedCategoryId === null || category.id === selectedCategoryId,
      )
      .map((category) => ({ category, products: category.products }))
      .filter((section) => section.products.length > 0);
  }, [categories, selectedCategoryId]);

  return {
    categories,
    countsByCategoryId,
    heroProducts,
    loading,
    sections,
    selectedCategoryId,
    setSelectedCategoryId,
    totalCount,
  };
}

export default useProductCatalog;
